import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import { TriangleAlert } from 'lucide-react';
import { ComponentProps } from 'react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Modal, ModalContent, ModalDescription, ModalTitle } from '../../../design-system/modal/modal';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { PayeeModalVariantConfig } from './payments-flow-modal';

type PayeeCheckModalProps = {
  isOpen: boolean;
  variantConfig: PayeeModalVariantConfig;
  isLoading: boolean;
  onClose: () => void;
  onEditDetails: () => Promise<void>;
  onContinue: () => void;
};

export const PayeeCheckModal = ({
  isOpen,
  variantConfig,
  isLoading,
  onClose,
  onEditDetails,
  onContinue,
}: PayeeCheckModalProps) => {
  const { title, renderDescription, ctaText } = getModalContent(variantConfig);

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent className="h-max w-11/12 sm:w-[520px]">
        <VisuallyHidden>
          <ModalTitle>Payee check outcome</ModalTitle>

          <ModalDescription>Payee check outcome</ModalDescription>
        </VisuallyHidden>

        <Stack space={4} align="center" className="bg-white p-7">
          <Inline justify="center" className="h-[120px] w-full py-5">
            <div className="h-20 w-20 rounded-full bg-seapoint-gray-background-modal p-6">
              <TriangleAlert strokeWidth={1.5} className="h-full w-full" />
            </div>
          </Inline>

          <Stack space={2} align="stretch" className="w-full">
            <h3 className="text-xl font-medium leading-5 tracking-tight text-black">{title}</h3>

            {renderDescription()}

            <p className="text-base font-medium leading-5 tracking-tight text-seapoint-gray">
              Only continue anyway if you're sure this is the right account.
            </p>
          </Stack>

          <Stack space={2} align="stretch" className="w-full">
            <Button variant="default" size="lg" isLoading={isLoading} onClick={onEditDetails} className="w-full">
              {ctaText}
            </Button>

            <Button variant="destructive" size="lg" onClick={onContinue}>
              Continue Anyway
            </Button>
          </Stack>
        </Stack>
      </ModalContent>
    </Modal>
  );
};

const getModalContent = (variantConfig: ComponentProps<typeof PayeeCheckModal>['variantConfig']) => {
  const { variant } = variantConfig;

  switch (variant) {
    case VerifyPayeeCheckResponseCode.CLOSE_MATCH:
      return {
        title: "The name you entered isn't the name on the account",
        renderDescription: () => (
          <p className="text-base font-medium leading-5 tracking-tight text-seapoint-gray">
            The name on the account is <span className="font-bold text-black">{variantConfig.name}</span>. Click change
            and continue and we'll change it to the right name for you.
          </p>
        ),
        ctaText: 'Change and Continue',
      };
    case VerifyPayeeCheckResponseCode.NO_MATCH:
      return {
        title: "The details on the account don't match",
        renderDescription: () => (
          <p className="text-base font-medium leading-5 tracking-tight text-seapoint-gray">
            The details on the account don't match the details you've entered. Double-check the details you have and try
            again.
          </p>
        ),
        ctaText: 'Edit details',
      };
    case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
    case VerifyPayeeCheckResponseCode.INVALID:
    case VerifyPayeeCheckResponseCode.GENERAL:
    case VerifyPayeeCheckResponseCode.INTERNAL_ERROR:
      return {
        title: "We're unable to verify these account details",
        renderDescription: () => (
          <p className="text-base font-medium leading-5 tracking-tight text-seapoint-gray">
            We can't check the account name right now and recommend you try again later.
          </p>
        ),
        ctaText: 'Edit details',
      };
    default:
      return exhaustiveCheck(variant);
  }
};
